import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import { Dimensions, Platform, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import colors, { appTheme } from '@/config/colors';
import { useDashboardContext } from '@/hooks/useDashboardContext';

import { PressableNeolexon } from '@/components/PressableNeolexon';
import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';

// constants
const SETS = [
  { id: 1, title: 'Wörterset' },
  { id: 2, title: 'Sätzeset' },
  { id: 3, title: 'Texteset' },
];

const SETS_NAVIGATION = {
  1: 'WordSet',
  2: 'SentenceSet',
  3: 'TextSet',
};

export function SetsModal({ isSetsModalVisible, changeSetsModalVisibility }) {
  // custom hooks
  const { navigate } = useNavigation();
  const { selectedID } = useDashboardContext();

  // state
  const [selectedSet, setSelectedSet] = useState(1);

  // methods
  const handleCancelPress = useCallback(() => {
    changeSetsModalVisibility(false);
  }, []);
  const handleNextPress = useCallback(() => {
    const screenName = SETS_NAVIGATION[selectedSet];
    if (!screenName) return;
    handleCancelPress();
    navigate(screenName, { selectedID });
  }, [selectedSet, selectedID]);
  const changeSelectedSet = useCallback((value) => {
    if (!value) return;
    setSelectedSet(value);
  }, []);
  const handleSelectedSetChange = useCallback((value) => {
    return () => {
      changeSelectedSet(value);
    };
  }, []);

  return (
    <Modal
      isVisible={isSetsModalVisible}
      onClose={handleCancelPress}
      title="Möchten Sie ein Wörter-, Sätze- oder Texteset erstellen?"
      confirmActionButton={<Button label="Weiter" onPress={handleNextPress} />}
      cancelActionButton={
        <Button
          label="Abbrechen"
          onPress={handleCancelPress}
          look="secondary"
        />
      }
    >
      <View style={styles.setsContainer}>
        {SETS.map((set, index) => {
          const notTheLast = index !== SETS.length - 1;
          const isSelected = selectedSet === set.id;
          return (
            <React.Fragment key={set.id}>
              <PressableNeolexon
                onPress={handleSelectedSetChange(set.id)}
                key={set.id}
              >
                <View
                  style={[styles.setItem, isSelected && styles.selectedSetItem]}
                >
                  <Text style={styles.setItemText}>{set.title}</Text>
                  <View style={styles.radio}>
                    {isSelected ? <View style={styles.radioActive} /> : null}
                  </View>
                </View>
              </PressableNeolexon>
              <View style={notTheLast ? styles.borderBottom : undefined} />
            </React.Fragment>
          );
        })}
      </View>
    </Modal>
  );
}

const isWeb = Platform.OS === 'web';
const styles = StyleSheet.create({
  modalStyle: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  containerFlex: {
    flex: 1,
  },
  container: {
    backgroundColor: colors.lightGrey,
    paddingVertical: 5,
    borderRadius: 2,
    width: isWeb
      ? Dimensions.get('window').width / 2.5
      : Dimensions.get('window').width / 1.5,
  },
  header: {
    borderBottomWidth: 1,
    borderBottomColor: colors.darkGrey,
    paddingVertical: 10,
  },
  headerText: {
    fontSize: 20,
    color: colors.darkGrey,
    textAlign: 'center',
  },
  body: {
    padding: 10,
  },
  setsContainer: {
    borderWidth: 1,
    borderColor: colors.darkGrey,
  },
  setItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
  },
  selectedSetItem: {
    backgroundColor: colors.grey,
  },
  setItemText: {
    fontSize: 16,
  },
  buttonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 20,
    flex: 1,
  },
  buttonContainer: {
    padding: 10,
    flex: 1,
    borderRadius: 2,
  },
  buttonText: {
    fontWeight: '500',
    textAlign: 'center',
  },
  backBtn: {
    backgroundColor: colors.grey,
  },
  backText: {
    color: appTheme.textColor,
  },
  nextBtn: {
    backgroundColor: appTheme.primary,
  },
  nextText: {
    color: colors.white,
  },
  radio: {
    borderWidth: 2,
    borderRadius: 50,
    borderColor: colors.darkGrey,
    height: 30,
    width: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioActive: {
    height: 17,
    width: 17,
    backgroundColor: appTheme.primary,
    borderRadius: 50,
  },
  marginRight: {
    marginRight: 5,
  },
  borderBottom: {
    borderBottomWidth: 1,
    borderBottomColor: colors.darkGrey,
  },
});
